import ErrorHandlerService from '@/modules/common/services/error-handler.service'
import { AxiosResponse } from 'axios'
import Container from 'typedi'
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import MediaService from '../services/media.service'
import type {
  CreateFontPackagePayload,
  DeleteFontPackagePayload,
  AllMedia,
  Media,
  MediaStoreUploadPayload,
  MediaStoreUploadResult,
  MediaUpload,
  MediaUploads
} from '@/interfaces'
import Vue from 'vue'
import { fileTypeInfo } from '@/bootstrap/global/file-types'

@Module({
  namespaced: true
})
export default class MediaStore extends VuexModule {
  media: any = {}
  mediaUploads: MediaUploads = {}
  allMediaUploadsProcessed = false
  allMediaUploadsCompleted = false
  hideUploadErrors = false

  // TODO: create a type.
  fonts: any[] = []

  get defaultFonts (): string[] {
    return [
      'Arvo',
      'Bitter',
      'Cormorant Garamond',
      'Crimson Pro',
      'Lato',
      'Libre Barcode 128',
      'Libre Barcode 128 Text',
      'Libre Barcode 39',
      'Libre Barcode 39 Text',
      'Libre Baskerville',
      'Lora',
      'Merriweather',
      'Merriweather Sans',
      'Montserrat',
      'Noto Serif',
      'Open Sans',
      'Oswald',
      'Outfit',
      'Playfair Display',
      'PT Sans',
      'PT Serif',
      'Raleway',
      'Roboto',
      'Roboto Condensed',
      'Slabo 27px'
    ]
  }

  get fontList () {
    return this.fonts
  }

  @Mutation
  SET_HIDE_UPLOAD_ERRORS (payload: boolean) {
    this.hideUploadErrors = payload
  }

  @Mutation
  SET_MEDIA (payload: AllMedia) {
    for (const mediaStatus in payload) {
      const mediaWithStatus = payload[mediaStatus as keyof AllMedia]
      mediaWithStatus.forEach((item: Media) => {
        const currentFileTypeInfo = fileTypeInfo(item.name)
        item.file_type_category = currentFileTypeInfo.category
        item.icon = currentFileTypeInfo.icon
        item.mime_type = currentFileTypeInfo.mimeType
      })
    }
    this.media = payload
  }

  @Mutation
  UPDATE_MEDIA_UPLOADS (payload: any) {
    const { name, status, url, progress } = payload
    Vue.set(this.mediaUploads, name, { status, url, progress } as MediaUpload)

    let allProcessed = true
    let allCompleted = true
    for (const fileName in this.mediaUploads) {
      const media: MediaUpload = this.mediaUploads[fileName]
      if (media.status === 'failed' || media.status === 'error' || media.status === 'virus detected') {
        allCompleted = false
      }
      if (media.status !== 'completed' && media.status !== 'failed' && media.status !== 'error' && media.status !== 'virus detected') {
        allProcessed = false
      }
    }

    this.allMediaUploadsProcessed = allProcessed
    this.allMediaUploadsCompleted = allCompleted
  }

  @Mutation
  REMOVE_MEDIA_UPLOADS (payload: string) {
    Vue.delete(this.mediaUploads, payload)
  }

  @Mutation
  SET_FONTS (payload: any) {
    this.fonts = payload
  }

  @Action({ rawError: true })
  async getMedia (payload: any) {
    const { event_uuid, include_usage } = payload
    const response = await Container.get(MediaService).getMedia(event_uuid, include_usage)
    this.context.commit('SET_MEDIA', response.data)
    return response
  }

  @Action({ rawError: true })
  async uploadMedia (payload: MediaStoreUploadPayload): Promise<MediaStoreUploadResult[]> {
    const { event_uuid, files, participant_uuid } = payload
    const uploads: MediaStoreUploadResult[] = []

    for (const file of files) {
      this.context.commit('UPDATE_MEDIA_UPLOADS', { name: file.name, status: 'pending', progress: 0 })
    }

    for (const file of files) {
      try {
        const config = {
          onUploadProgress: (progressEvent: any) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            this.context.commit('UPDATE_MEDIA_UPLOADS', { name: file.name, status: progress === 100 ? 'uploaded' : 'uploading', progress })
          }
        }
        const response = await Container.get(MediaService).uploadMedia(event_uuid, file, participant_uuid, config)
        const responseEventMedia = response.responseEventMedia as AxiosResponse

        if (responseEventMedia?.data) {
          const videoConversionUuid = responseEventMedia.data.video_conversion_uuid
          if (videoConversionUuid) {
            // Check the video conversion status every 5 seconds if a video_conversion_uuid is returned.
            this.context.commit('UPDATE_MEDIA_UPLOADS', { name: file.name, status: 'processing' })

            const checkStatus = setInterval(async () => {
              const statusResponse: AxiosResponse = await this.context.dispatch('getVideoConversionStatus', { event_uuid, video_conversion_uuid: videoConversionUuid })
              this.context.commit('UPDATE_MEDIA_UPLOADS', { name: file.name, url: responseEventMedia.data.url, status: statusResponse.data.status })
              if (statusResponse.data.status === 'completed' || statusResponse.data.status === 'failed' || statusResponse.data.status === 'error') {
                clearInterval(checkStatus)
              }
            }, 5000)
          } else {
            // If no video_conversion_uuid, mark the upload as completed.
            this.context.commit('UPDATE_MEDIA_UPLOADS', { name: file.name, url: responseEventMedia.data.url, status: 'completed' })
          }
          uploads.push({
            fileName: file.name,
            url: responseEventMedia.data.url,
            contentType: file.type
          })
        } else {
          this.context.commit('UPDATE_MEDIA_UPLOADS', { name: file.name, status: 'error' })
        }
      } catch (error: any) {
        const virusDetected = error.data?.error_code === 'VIRUS_DETECTED'
        this.context.commit('UPDATE_MEDIA_UPLOADS', { name: file.name, status: virusDetected ? 'virus detected' : 'error' })
        if (!this.hideUploadErrors || virusDetected) {
          Container.get(ErrorHandlerService).error(error)
        }
      }
    }

    return uploads
  }

  @Action({ rawError: true })
  async getVideoConversionStatus (payload: any) {
    const { event_uuid, video_conversion_uuid } = payload
    const response = await Container.get(MediaService).getVideoConversionStatus(event_uuid, video_conversion_uuid)
    return response
  }

  @Action({ rawError: true })
  clearFinishedMediaUploads () {
    for (const fileName in this.mediaUploads) {
      if (this.mediaUploads[fileName].status === 'completed' || this.mediaUploads[fileName].status === 'failed' || this.mediaUploads[fileName].status === 'error') {
        this.context.commit('REMOVE_MEDIA_UPLOADS', fileName)
      }
    }
  }

  @Action({ rawError: true })
  async removeMedia (payload: any) {
    const { event_uuid, file_identifier } = payload
    const response = await Container.get(MediaService).deleteMedia(event_uuid, file_identifier)
    return response
  }

  // TODO: create types for these payloads.
  @Action({ rawError: true })
  async getFontStores (companyUuid: string) {
    const response = await Container.get(MediaService).getCompanyFontStore(companyUuid)
    this.context.commit('SET_FONTS', response.data)
    return response
  }

  /**
   * calls the patch request from the media service and returns the response.
   * @param {any} the font package and font package uuid
   * @returns {AxiosResponse}
   */
  // @Action({ rawError: true })
  // async updateFontPackage ({ companyUuid, updatedFontPackage, fontPackageUuid }: UpdateFontPackagePayload) {
  //   try {
  //     const response = await Container.get(MediaService).updateFontInFontStore(companyUuid, updatedFontPackage, fontPackageUuid)
  //     this.context.dispatch('getFontStores', companyUuid)
  //     return response
  //   } catch (err) {
  //     Container.get(ErrorHandlerService).error(err)
  //   }
  // }

  @Action({ rawError: true })
  async createFontPackage ({ companyUuid, fontPackage }: CreateFontPackagePayload) {
    const response = await Container.get(MediaService).uploadFontToFontStore(companyUuid, fontPackage)
    this.context.dispatch('getFontStores', companyUuid)
    return response
  }

  @Action({ rawError: true })
  async deleteFontPackage ({ fontPackageUuid, companyUuid }: DeleteFontPackagePayload) {
    const response = await Container.get(MediaService).deleteFontStore(fontPackageUuid, companyUuid)
    this.context.dispatch('getFontStores', companyUuid)
    return response
  }
}
